
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, MessageSquare, Users, Calendar, Award, Settings, Sun, Moon, LogOut, PlusCircle } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/context/AuthContext';
import AddNewDialog from '@/components/AddNewDialog';
import { cn } from '@/lib/utils';
import { logger } from '@/utils/logger';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleSignOut = async () => {
    try {
      logger.log("Sign out initiated");
      await signOut();
      logger.log("Sign out completed, navigating to auth");
      navigate('/login');
    } catch (error) {
      logger.error("Error during sign out:", error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-card overflow-hidden max-w-[100vw] w-full">
      {/* Status bar background */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-card w-full">
        <div className="safe-area-top" />
      </div>
      
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-card w-full">
        <div className="w-full pt-[env(safe-area-inset-top,0px)] border-b border-border">
          <div className="flex justify-between items-center px-4 md:px-5 py-4 max-w-screen-2xl mx-auto">
            <Link to="/app" className="flex items-center gap-2 text-xl md:text-2xl font-semibold text-primary hover:opacity-80 transition-opacity">
              <img src="/images/logo.png" alt="KidQuotes Logo" className="h-6 w-6 md:h-8 md:w-8" />
              KidQuotes
            </Link>
            <div className="flex items-center gap-2 md:gap-3">
              <AddNewDialog>
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2"
                >
                  <PlusCircle className="h-5 w-5 md:h-6 md:w-6" />
                </Button>
              </AddNewDialog>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="p-2"
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5 md:h-6 md:w-6" />
                ) : (
                  <Moon className="h-5 w-5 md:h-6 md:w-6" />
                )}
              </Button>
              <Link to="/search">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2"
                >
                  <Search className="h-5 w-5 md:h-6 md:w-6" />
                </Button>
              </Link>
              <Link to="/settings">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-2"
                >
                  <Settings className="h-5 w-5 md:h-6 md:w-6" />
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSignOut}
                className="p-2 text-destructive cursor-pointer"
                type="button"
              >
                <LogOut className="h-5 w-5 md:h-6 md:w-6" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <main className={cn(
        "flex-1 px-3 sm:px-4 md:px-6 pb-24 md:pb-28 overflow-auto w-full",
        "mt-[calc(env(safe-area-inset-top,0px)+88px)] md:mt-[calc(env(safe-area-inset-top,0px)+96px)]",
        "max-w-screen-2xl mx-auto"
      )}>
        {children}
      </main>
      
      {/* Footer navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-40 w-full">
        <nav className="flex justify-around items-center p-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] max-w-screen-2xl mx-auto">
          <Link to="/app" className={`flex flex-col items-center p-1 sm:p-2 ${isActive('/app') ? 'text-primary' : 'text-muted-foreground'}`}>
            <Home className="h-5 w-5" />
            <span className="text-xs mt-1">Home</span>
          </Link>
          <Link to="/quotes" className={`flex flex-col items-center p-1 sm:p-2 ${isActive('/quotes') ? 'text-primary' : 'text-muted-foreground'}`}>
            <MessageSquare className="h-5 w-5" />
            <span className="text-xs mt-1">Quotes</span>
          </Link>
          <Link to="/children" className={`flex flex-col items-center p-1 sm:p-2 ${isActive('/children') ? 'text-primary' : 'text-muted-foreground'}`}>
            <Users className="h-5 w-5" />
            <span className="text-xs mt-1">Children</span>
          </Link>
          <Link to="/on-this-day" className={`flex flex-col items-center p-1 sm:p-2 ${isActive('/on-this-day') ? 'text-primary' : 'text-muted-foreground'}`}>
            <Calendar className="h-5 w-5" />
            <span className="text-xs mt-1">On This Day</span>
          </Link>
          <Link to="/milestones" className={`flex flex-col items-center p-1 sm:p-2 ${isActive('/milestones') ? 'text-primary' : 'text-muted-foreground'}`}>
            <Award className="h-5 w-5" />
            <span className="text-xs mt-1">Milestones</span>
          </Link>
        </nav>
        <div className="safe-area-bottom" />
      </div>
    </div>
  );
};

export default Layout;